import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { blue, green, grey, orange, red } from "@mui/material/colors";
import ReactECharts from "echarts-for-react";
import { LegendChip } from "./LegendChip";

// --- Semantic color tokens for the progress bar ---
const neutral = { text: grey[900], muted: grey[600], bg: grey[200] };
const axis = {
	line: grey[400],
	label: grey[600],
	marker: grey[900],
	markerText: "#fff",
};
const scheduled = { color: grey[400], text: neutral.text };
const current = { color: blue[200], text: blue[800] };
const projected = { color: orange[200], text: orange[900] };
const varianceRed = { bg: red[100], text: red[800] };
const varianceGreen = { bg: green[100], text: green[800] };
const unscheduled = { bg: varianceRed.bg, text: varianceRed.text };

type Formatted = {
	value: string;
	color: string;
	tooltip: string | undefined;
};

export const formatVariance = (hours: number | null | undefined): Formatted => {
	if (hours == null || !Number.isFinite(hours)) {
		return { value: "-", color: neutral.text, tooltip: undefined };
	}
	const rounded = Math.round(hours * 10) / 10;
	if (rounded === 0) {
		return {
			value: "0hs",
			color: neutral.text,
			tooltip: "Projected to finish on time.",
		};
	}
	const abs = Math.round(Math.abs(rounded));
	if (rounded > 0) {
		return {
			value: `+${abs}hs`,
			color: varianceRed.text,
			tooltip: `Projected to finish ${abs}hs after the scheduled event end.`,
		};
	}
	return {
		value: `-${abs}hs`,
		color: varianceGreen.text,
		tooltip: `Projected to finish ${abs}hs before the scheduled event end.`,
	};
};

type BarItem = {
	yIndex: number;
	startMs: number;
	endMs: number;
	bg: string;
	fg: string;
	label: string;
	tooltip: string;
};

type Props = {
	eventStart: Date | null | undefined;
	scheduledEnd: Date | null | undefined;
	projectedEnd: Date | null | undefined;
	scheduledHours: number | null | undefined;
	currentHours: number | null | undefined;
	remainingHours: number | null | undefined;
	varianceHours: number | null | undefined;
};

export const HoursProgressBar = ({
	eventStart,
	scheduledEnd,
	projectedEnd,
	scheduledHours,
	currentHours,
	remainingHours,
	varianceHours,
}: Props) => {
	if (!eventStart) {
		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
				<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
					Scheduled Variance
				</Typography>
				<Typography variant="caption" sx={{ color: neutral.muted }}>
					-
				</Typography>
			</Box>
		);
	}

	const isUnscheduled = !scheduledEnd;

	const now = new Date();
	const currentHrs = currentHours != null ? Math.round(currentHours) : 0;

	if (isUnscheduled) {
		const unscheduledBars: BarItem[] = [
			{
				yIndex: 0,
				startMs: eventStart.getTime(),
				endMs: now.getTime(),
				bg: current.color,
				fg: current.text,
				label: `${currentHrs}h`,
				tooltip: `Current: ${dxtToLocalServerTime(eventStart, "MM/dd HH:mm")} → ${dxtToLocalServerTime(now, "MM/dd HH:mm")} (${currentHrs} h elapsed)`,
			},
		];

		const unscheduledOption = {
			animation: false,
			grid: { top: 22, right: 35, bottom: 28, left: 35 },
			xAxis: {
				type: "time" as const,
				offset: 6,
				axisLabel: {
					fontSize: 10,
					color: axis.label,
					formatter: (val: number) =>
						dxtToLocalServerTime(new Date(val), "MM/dd HH:mm"),
					hideOverlap: false,
					customValues: [eventStart.getTime(), now.getTime()],
				},
				axisTick: {
					show: true,
					customValues: [eventStart.getTime(), now.getTime()],
					length: 5,
					inside: true,
					lineStyle: { color: axis.line },
				},
				axisLine: { show: true, lineStyle: { color: axis.line } },
				splitLine: { show: false },
			},
			yAxis: {
				type: "category" as const,
				data: ["Actual"],
				axisTick: { show: false },
				axisLine: { show: false },
				axisLabel: { show: false },
			},
			tooltip: {
				trigger: "item" as const,
				formatter: (params: { data?: { tooltip?: string } }) =>
					params.data?.tooltip ?? "",
			},
			series: [
				{
					type: "custom" as const,
					z: 2,
					renderItem: (
						_params: unknown,
						api: {
							value: (n: number) => number;
							coord: (xy: [number, number]) => [number, number];
							size: (xy: [number, number]) => [number, number];
							style: () => Record<string, unknown>;
						},
					) => {
						const yIdx = api.value(0);
						const startMs = api.value(1);
						const endMs = api.value(2);
						const start = api.coord([startMs, yIdx]);
						const end = api.coord([endMs, yIdx]);
						const rowH = api.size([0, 1])[1];
						const h = Math.max(18, rowH * 0.75);
						const width = Math.max(2, end[0] - start[0]);
						const cx = (start[0] + end[0]) / 2;

						return {
							type: "group",
							children: [
								{
									type: "rect",
									shape: {
										x: start[0],
										y: start[1] - h / 2,
										width,
										height: h,
										r: 3,
									},
									style: api.style(),
								},
								{
									type: "text",
									style: {
										x: cx,
										y: start[1],
										text: api.value(3) as unknown as string,
										textAlign: "center",
										textVerticalAlign: "middle",
										fontSize: 10,
										fontWeight: 600,
										fill: api.value(4) as unknown as string,
										overflow: "truncate",
										width: width - 6,
									},
								},
							],
						};
					},
					encode: { x: [1, 2], y: 0, tooltip: [1, 2] },
					data: unscheduledBars.map((b) => ({
						value: [b.yIndex, b.startMs, b.endMs, b.label, b.fg],
						itemStyle: { color: b.bg },
						tooltip: b.tooltip,
					})),
				},
				{
					type: "custom" as const,
					z: 5,
					silent: true,
					clip: false,
					renderItem: (
						_params: unknown,
						api: {
							value: (n: number) => number;
							coord: (xy: [number, number]) => [number, number];
							size: (xy: [number, number]) => [number, number];
						},
					) => {
						const ms = api.value(1);
						const row = api.coord([ms, 0]);
						const rowH = api.size([0, 1])[1];
						const x = row[0];
						const yTop = row[1] - rowH / 2 - 4;
						const yBottom = row[1] + rowH / 2;

						const chipW = 32;
						const chipH = 14;
						return {
							type: "group",
							children: [
								{
									type: "line",
									shape: { x1: x, y1: yTop, x2: x, y2: yBottom },
									style: {
										stroke: axis.marker,
										lineDash: [4, 4],
										lineWidth: 1,
									},
								},
								{
									type: "rect",
									shape: {
										x: x - chipW / 2,
										y: yTop - chipH - 2,
										width: chipW,
										height: chipH,
										r: 2,
									},
									style: { fill: axis.marker },
								},
								{
									type: "text",
									style: {
										x,
										y: yTop - chipH / 2 - 2,
										text: "NOW",
										fontSize: 9,
										fontWeight: 700,
										fill: axis.markerText,
										textAlign: "center",
										textVerticalAlign: "middle",
									},
								},
							],
						};
					},
					encode: { x: [1], y: 0 },
					data: [{ value: [0, now.getTime()] }],
				},
			],
		};

		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
				<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
					Scheduled Variance
				</Typography>

				<Chip
					label="Current event was not scheduled"
					size="small"
					sx={{
						alignSelf: "flex-start",
						fontWeight: 600,
						fontSize: "0.7rem",
						height: 20,
						mt: 0.5,
						bgcolor: unscheduled.bg,
						color: unscheduled.text,
						"& .MuiChip-label": { px: 1 },
					}}
				/>

				<ReactECharts
					option={unscheduledOption}
					style={{ height: 65, width: "100%", overflow: "visible" }}
					notMerge
					lazyUpdate
					opts={{ renderer: "svg" }}
				/>

				<Box
					sx={{
						display: "flex",
						gap: 1.25,
						flexWrap: "wrap",
						mt: 0.25,
						rowGap: 0.25,
					}}
				>
					<LegendChip
						color={current.color}
						label="Current"
						value={`${currentHrs}h`}
					/>
				</Box>
			</Box>
		);
	}

	const variance = formatVariance(varianceHours);
	const isOverrun = (varianceHours ?? 0) > 0;
	const isAhead = (varianceHours ?? 0) < 0;

	const scheduledHrs = scheduledHours != null ? Math.round(scheduledHours) : 0;
	const projRemainingHrs =
		remainingHours != null ? Math.round(Math.max(0, remainingHours)) : 0;

	// biome-ignore lint: scheduledEnd and projectedEnd are guaranteed non-null past the unscheduled guard
	const safeScheduledEnd = scheduledEnd!;
	// biome-ignore lint: scheduledEnd and projectedEnd are guaranteed non-null past the unscheduled guard
	const safeProjectedEnd = projectedEnd!;

	const varianceStartMs = isOverrun
		? safeScheduledEnd.getTime()
		: safeProjectedEnd.getTime();
	const varianceEndMs = isOverrun
		? safeProjectedEnd.getTime()
		: safeScheduledEnd.getTime();
	const varianceBg = isOverrun
		? varianceRed.bg
		: isAhead
			? varianceGreen.bg
			: neutral.bg;
	const varianceText = isOverrun
		? varianceRed.text
		: isAhead
			? varianceGreen.text
			: neutral.muted;
	const hasVariance =
		(varianceHours ?? 0) !== 0 && varianceStartMs !== varianceEndMs;

	const categories = ["Variance", "Actual", "Scheduled"];

	const bars: BarItem[] = [
		{
			yIndex: 2,
			startMs: eventStart.getTime(),
			endMs: safeScheduledEnd.getTime(),
			bg: scheduled.color,
			fg: scheduled.text,
			label: `${scheduledHrs}h`,
			tooltip: `Scheduled: ${dxtToLocalServerTime(eventStart, "MM/dd HH:mm")} → ${dxtToLocalServerTime(safeScheduledEnd, "MM/dd HH:mm")} (${scheduledHrs} h)`,
		},
		{
			yIndex: 1,
			startMs: eventStart.getTime(),
			endMs: now.getTime(),
			bg: current.color,
			fg: current.text,
			label: `${currentHrs}h`,
			tooltip: `Current: ${dxtToLocalServerTime(eventStart, "MM/dd HH:mm")} → ${dxtToLocalServerTime(now, "MM/dd HH:mm")} (${currentHrs} h elapsed)`,
		},
	];

	if (projRemainingHrs > 0 && now.getTime() < safeProjectedEnd.getTime()) {
		bars.push({
			yIndex: 1,
			startMs: now.getTime(),
			endMs: safeProjectedEnd.getTime(),
			bg: projected.color,
			fg: projected.text,
			label: `${projRemainingHrs}h`,
			tooltip: `Projected remaining: ${dxtToLocalServerTime(now, "MM/dd HH:mm")} → ${dxtToLocalServerTime(safeProjectedEnd, "MM/dd HH:mm")} (${projRemainingHrs} h)`,
		});
	}

	if (hasVariance) {
		bars.push({
			yIndex: 0,
			startMs: varianceStartMs,
			endMs: varianceEndMs,
			bg: varianceBg,
			fg: varianceText,
			label: variance.value,
			tooltip: `Variance: ${variance.value} — ${variance.tooltip ?? ""}`,
		});
	}

	const requiredTimestamps = [
		eventStart.getTime(),
		safeScheduledEnd.getTime(),
		safeProjectedEnd.getTime(),
	];
	const sortedTimestamps = [...requiredTimestamps].sort((a, b) => a - b);
	const startTs = sortedTimestamps[0];
	const endTs = sortedTimestamps[sortedTimestamps.length - 1];
	const middleTs = sortedTimestamps[1];
	const axisSpan = endTs - startTs;
	const overlapThreshold = axisSpan * 0.18;
	const middleOverlaps =
		middleTs - startTs < overlapThreshold ||
		endTs - middleTs < overlapThreshold;
	const visibleTimestamps = middleOverlaps
		? [startTs, endTs]
		: [startTs, middleTs, endTs];

	const option = {
		animation: false,
		grid: { top: 22, right: 35, bottom: 28, left: 35 },
		xAxis: {
			type: "time" as const,
			offset: 6,
			axisLabel: {
				fontSize: 10,
				color: axis.label,
				formatter: (val: number) =>
					dxtToLocalServerTime(new Date(val), "MM/dd HH:mm"),
				hideOverlap: false,
				customValues: visibleTimestamps,
			},
			axisTick: {
				show: true,
				customValues: visibleTimestamps,
				length: 5,
				inside: true,
				lineStyle: { color: axis.line },
			},
			axisLine: { show: true, lineStyle: { color: axis.line } },
			splitLine: { show: false },
		},
		yAxis: {
			type: "category" as const,
			data: categories,
			axisTick: { show: false },
			axisLine: { show: false },
			axisLabel: { show: false },
		},
		tooltip: {
			trigger: "item" as const,
			formatter: (params: { data?: { tooltip?: string } }) =>
				params.data?.tooltip ?? "",
		},
		series: [
			{
				type: "custom" as const,
				z: 2,
				renderItem: (
					_params: unknown,
					api: {
						value: (n: number) => number;
						coord: (xy: [number, number]) => [number, number];
						size: (xy: [number, number]) => [number, number];
						style: () => Record<string, unknown>;
					},
				) => {
					const yIdx = api.value(0);
					const startMs = api.value(1);
					const endMs = api.value(2);
					const start = api.coord([startMs, yIdx]);
					const end = api.coord([endMs, yIdx]);
					const rowH = api.size([0, 1])[1];
					const h = Math.max(18, rowH * 0.75);
					const width = Math.max(2, end[0] - start[0]);
					const cx = (start[0] + end[0]) / 2;

					return {
						type: "group",
						children: [
							{
								type: "rect",
								shape: {
									x: start[0],
									y: start[1] - h / 2,
									width,
									height: h,
									r: 3,
								},
								style: api.style(),
							},
							{
								type: "text",
								style: {
									x: cx,
									y: start[1],
									text: api.value(3) as unknown as string,
									textAlign: "center",
									textVerticalAlign: "middle",
									fontSize: 10,
									fontWeight: 600,
									fill: api.value(4) as unknown as string,
									overflow: "truncate",
									width: width - 6,
								},
							},
						],
					};
				},
				encode: { x: [1, 2], y: 0, tooltip: [1, 2] },
				data: bars.map((b) => ({
					value: [b.yIndex, b.startMs, b.endMs, b.label, b.fg],
					itemStyle: { color: b.bg },
					tooltip: b.tooltip,
				})),
			},
			// NOW marker
			{
				type: "custom" as const,
				z: 5,
				silent: true,
				clip: false,
				renderItem: (
					_params: unknown,
					api: {
						value: (n: number) => number;
						coord: (xy: [number, number]) => [number, number];
						size: (xy: [number, number]) => [number, number];
					},
				) => {
					const ms = api.value(1);
					const topRow = api.coord([ms, 2]);
					const bottomRow = api.coord([ms, 0]);
					const rowH = api.size([0, 1])[1];
					const x = topRow[0];
					const yTop = topRow[1] - rowH / 2 - 4;
					const yBottom = bottomRow[1] + rowH / 2;

					const chipW = 32;
					const chipH = 14;
					return {
						type: "group",
						children: [
							{
								type: "line",
								shape: { x1: x, y1: yTop, x2: x, y2: yBottom },
								style: {
									stroke: axis.marker,
									lineDash: [4, 4],
									lineWidth: 1,
								},
							},
							{
								type: "rect",
								shape: {
									x: x - chipW / 2,
									y: yTop - chipH - 2,
									width: chipW,
									height: chipH,
									r: 2,
								},
								style: { fill: axis.marker },
							},
							{
								type: "text",
								style: {
									x,
									y: yTop - chipH / 2 - 2,
									text: "NOW",
									fontSize: 9,
									fontWeight: 700,
									fill: axis.markerText,
									textAlign: "center",
									textVerticalAlign: "middle",
								},
							},
						],
					};
				},
				encode: { x: [1], y: 0 },
				data: [{ value: [0, now.getTime()] }],
			},
		],
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
			<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
				Scheduled Variance
			</Typography>

			<ReactECharts
				option={option}
				style={{ height: 105, width: "100%", overflow: "visible" }}
				notMerge
				lazyUpdate
				opts={{ renderer: "svg" }}
			/>

			<Box
				sx={{
					display: "flex",
					gap: 1.25,
					flexWrap: "wrap",
					mt: 0.25,
					rowGap: 0.25,
				}}
			>
				<LegendChip
					color={scheduled.color}
					label="Scheduled"
					value={`${scheduledHrs}h`}
				/>
				<LegendChip
					color={current.color}
					label="Current"
					value={`${currentHrs}h`}
				/>
				{projRemainingHrs > 0 && now.getTime() < safeProjectedEnd.getTime() && (
					<LegendChip
						color={projected.color}
						label="Projected Remaining"
						value={`${projRemainingHrs}h`}
					/>
				)}
				{hasVariance && (
					<Tooltip
						arrow
						placement="top"
						title={`Variance: ${variance.value} — ${variance.tooltip ?? "no deviation projected."}`}
					>
						<Chip
							label={`Variance ${variance.value}`}
							size="small"
							color={isOverrun ? "error" : isAhead ? "success" : "default"}
							sx={{
								fontWeight: 700,
								fontSize: "0.7rem",
								height: 18,
								cursor: "help",
								"& .MuiChip-label": { px: 0.75 },
							}}
						/>
					</Tooltip>
				)}
			</Box>
		</Box>
	);
};
